'use client'

import { useState, useRef, useLayoutEffect, forwardRef, useCallback } from 'react'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { cx } from 'class-variance-authority'

type Item = {
    label: string
    value: string
}

type ToggleGroupProps = {
    items: Item[] | Item[][]
    defaultValue?: string
    onChange?: (value: string) => void
}

type HoverState = {
    value: string | null
    x: number
    y: number
    width: number
    height: number
}

type AnimationMode = 'none' | 'appearing' | 'following' | 'disappearing'

function isGroupedItems(items: Item[] | Item[][]): items is Item[][] {
    return Array.isArray(items[0])
}

const HOVER_PADDING_X = 12
const HOVER_PADDING_Y = 8

export const ToggleGroup: React.FC<ToggleGroupProps> = ({ items, defaultValue, onChange }) => {
    const flatItems = isGroupedItems(items) ? items.flat() : items
    const initialDefaultValue = defaultValue || flatItems[0]?.value

    const [activeValue, setActiveValue] = useState(initialDefaultValue || '')
    const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({
        transform: 'translateX(0)',
        width: 0,
        opacity: 0,
        transition: 'none'
    })

    const [hoverState, setHoverState] = useState<HoverState>({
        value: null,
        x: 0,
        y: 0,
        width: 0,
        height: 0
    })
    const [animationMode, setAnimationMode] = useState<AnimationMode>('none')

    const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
    const labelRefs = useRef<Map<string, HTMLSpanElement>>(new Map())
    const isInitialRender = useRef(true)
    const containerRef = useRef<HTMLDivElement>(null)
    const animationTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

    const updateIndicatorStyle = (value: string, animate = true) => {
        const el = itemRefs.current.get(value)
        if (el) {
            el.scrollIntoView({ behavior: animate ? 'smooth' : 'auto', block: 'nearest', inline: 'center' })
            setIndicatorStyle({
                transform: `translateX(${el.offsetLeft}px)`,
                width: `${el.offsetWidth}px`,
                opacity: 1,
                transition: animate ? 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
            })
        }
    }

    useLayoutEffect(() => {
        const timer = setTimeout(() => {
            if (isInitialRender.current && initialDefaultValue) {
                const el = itemRefs.current.get(initialDefaultValue)
                if (el) {
                    setIndicatorStyle({
                        transform: `translateX(${el.offsetLeft}px)`,
                        width: `${el.offsetWidth}px`,
                        opacity: 1,
                        transition: 'none'
                    })
                }
                isInitialRender.current = false
            }
        }, 0)

        return () => clearTimeout(timer)
    }, [initialDefaultValue])

    useLayoutEffect(() => {
        if (isInitialRender.current) return
        updateIndicatorStyle(activeValue, true)
    }, [activeValue])

    const handleValueChange = (value: string) => {
        setActiveValue(value)
        onChange?.(value)
    }

    const setItemRef = (value: string, el: HTMLButtonElement | null) => {
        if (el) {
            itemRefs.current.set(value, el)
        } else {
            itemRefs.current.delete(value)
        }
    }

    const setLabelRef = (value: string, el: HTMLSpanElement | null) => {
        if (el) labelRefs.current.set(value, el)
        else labelRefs.current.delete(value)
    }

    const getItemPosition = useCallback((value: string) => {
        const label = labelRefs.current.get(value)
        const container = containerRef.current
        if (!label || !container) return null

        const containerRect = container.getBoundingClientRect()
        const labelRect = label.getBoundingClientRect()

        return {
            x: labelRect.left - containerRect.left - HOVER_PADDING_X,
            y: labelRect.top - containerRect.top - HOVER_PADDING_Y,
            width: labelRect.width + HOVER_PADDING_X * 2,
            height: labelRect.height + HOVER_PADDING_Y * 2
        }
    }, [])

    const handleMouseEnter = useCallback(
        (value: string) => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current)
            }

            const position = getItemPosition(value)
            if (!position) return

            const wasHovering = hoverState.value !== null
            const newHoverState: HoverState = {
                value,
                ...position
            }

            if (wasHovering) {
                setAnimationMode('following')
                setHoverState(newHoverState)
            } else {
                setHoverState(newHoverState)
                setAnimationMode('appearing')

                animationTimeoutRef.current = setTimeout(() => {
                    setAnimationMode('following')
                }, 16)
            }
        },
        [hoverState.value, getItemPosition]
    )

    const handleMouseLeave = useCallback(() => {
        if (animationTimeoutRef.current) {
            clearTimeout(animationTimeoutRef.current)
        }

        setAnimationMode('disappearing')

        animationTimeoutRef.current = setTimeout(() => {
            setHoverState((prev) => ({ ...prev, value: null }))
            setAnimationMode('none')
        }, 100)
    }, [])

    useLayoutEffect(() => {
        return () => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current)
            }
        }
    }, [])

    const getHoverStyle = (): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            transform: `translate(${hoverState.x}px, ${hoverState.y}px)`,
            width: `${hoverState.width}px`,
            height: `${hoverState.height}px`
        }

        switch (animationMode) {
            case 'appearing':
                return {
                    ...baseStyle,
                    opacity: 0,
                    transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)'
                }
            case 'following':
                return {
                    ...baseStyle,
                    opacity: 1,
                    transition:
                        'transform 200ms cubic-bezier(0.4, 0, 0.2, 1), width 200ms cubic-bezier(0.4, 0, 0.2, 1), height 200ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)'
                }
            case 'disappearing':
                return {
                    ...baseStyle,
                    opacity: 0,
                    transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)'
                }
            default:
                return {
                    ...baseStyle,
                    opacity: 0
                }
        }
    }

    return (
        <ToggleGroupPrimitive.Root
            type="single"
            defaultValue={initialDefaultValue}
            onValueChange={handleValueChange}
            className="h-[48px] relative flex w-full border-b border-borderPrimary no-scrollbar"
            ref={containerRef}
        >
            {hoverState.value && (
                <div
                    className="absolute bg-backgroundEmphasis rounded-lg pointer-events-none z-0"
                    style={getHoverStyle()}
                />
            )}

            {isGroupedItems(items) ? (
                <div className="flex w-full justify-between gap-8">
                    {items.map((group, groupIndex) => (
                        <div key={groupIndex} className="flex min-w-fit">
                            {group.map((item) => (
                                <ToggleGroupItem
                                    key={item.value}
                                    item={item}
                                    ref={(el) => setItemRef(item.value, el)}
                                    setLabelRef={setLabelRef}
                                    hovered={hoverState.value === item.value}
                                    selected={activeValue === item.value}
                                    onMouseEnter={() => handleMouseEnter(item.value)}
                                    onMouseLeave={handleMouseLeave}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex min-w-fit">
                    {flatItems.map((item) => (
                        <ToggleGroupItem
                            key={item.value}
                            item={item}
                            ref={(el) => setItemRef(item.value, el)}
                            setLabelRef={setLabelRef}
                            hovered={hoverState.value === item.value}
                            selected={activeValue === item.value}
                            onMouseEnter={() => handleMouseEnter(item.value)}
                            onMouseLeave={handleMouseLeave}
                        />
                    ))}
                </div>
            )}

            <div className="absolute bottom-[-1px] h-0.5 bg-backgroundPositiveStrong" style={indicatorStyle} />
        </ToggleGroupPrimitive.Root>
    )
}

const ToggleGroupItem = forwardRef<
    HTMLButtonElement,
    {
        item: Item
        setLabelRef: (value: string, el: HTMLSpanElement | null) => void
        hovered: boolean
        selected: boolean
        onMouseEnter: () => void
        onMouseLeave: () => void
    }
>(({ item, setLabelRef, hovered, selected, onMouseEnter, onMouseLeave }, ref) => {
    return (
        <ToggleGroupPrimitive.Item
            ref={ref}
            value={item.value}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={cx(
                'relative h-[48px] text-base font-medium min-w-fit transition-colors px-3 py-2 rounded-md flex items-center justify-center',
                selected ? 'text-textSelect' : hovered ? 'text-textSelect' : 'text-textSecondary hover:text-textSelect'
            )}
        >
            <span ref={(el) => setLabelRef(item.value, el)} className="whitespace-nowrap">
                {item.label}
            </span>
        </ToggleGroupPrimitive.Item>
    )
})

export default ToggleGroup
